import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Mail, Phone, Linkedin, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import { useEmailCompose } from "@/context/EmailComposeContext";

const FOOTER_PEC = "greenhousesolutionsspa@pec.it";

async function copyPec() {
  const value = FOOTER_PEC;
  const fallbackCopy = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, value.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  };
  let ok = false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      ok = true;
    }
  } catch { /* fall through */ }
  if (!ok) ok = fallbackCopy();
  if (ok) toast.success("PEC copiata negli appunti");
  else toast.error("Impossibile copiare la PEC", { description: value });
}

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/greenhouse-solutions-spa/", testid: "footer-social-linkedin" },
  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/share/18xSY7wsha/", testid: "footer-social-facebook" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/ghs.spa?utm_source=qr&igsh=em92MnJudXR3dzdz", testid: "footer-social-instagram" },
];

export default function Footer() {
  const { open: openEmail } = useEmailCompose();
  const mailBtnCls = "flex items-start gap-2 hover:text-background transition-colors break-all text-left w-full cursor-pointer bg-transparent p-0 border-0";
  return (
    <footer data-testid="main-footer" className="bg-foreground text-background">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <img
              src="/images/ghs-logo-icon-white.png?v=2"
              alt=""
              width="636"
              height="204"
              data-testid="footer-logo-icon"
              className="h-28 md:h-36 w-auto max-w-full object-contain object-left mb-5"
              loading="lazy"
            />
            <div className="flex items-center gap-2 mb-3">
              <span className="w-3 h-3 bg-primary block" />
              <span className="font-head font-black text-xl tracking-tightest">GREENHOUSE SOLUTIONS SPA</span>
            </div>
            <p data-testid="footer-slogan" className="font-head font-black text-base md:text-lg tracking-tightest mb-6">
              Costruiamo con <span className="text-primary">visione</span>.
            </p>
            <p className="text-background/60 max-w-md leading-relaxed">
              Ristrutturazioni chiavi in mano, impermeabilizzazione in poliurea, coibentazione
              in poliuretano, efficientamento energetico, pareti ventilate, rivestimenti per
              piscine, rivestimenti protettivi anticorrosivi, restauro conservativo e finiture
              e rivestimenti con risanamento dell’umidità di risalita. Operiamo in tutta la
              Puglia e in tutta Italia.
            </p>
            <Link
              to="/preventivo"
              data-testid="footer-cta"
              className="mt-8 inline-flex items-center gap-2 font-head text-3xl md:text-4xl font-black tracking-tightest hover:text-primary transition-colors"
            >
              Richiedi un preventivo <ArrowUpRight className="w-8 h-8" />
            </Link>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-5">Servizi</p>
            <ul className="space-y-3 text-background/70">
              <li><Link to="/ristrutturazioni-chiavi-in-mano" className="hover:text-background transition-colors">Ristrutturazioni Chiavi in Mano</Link></li>
              <li><Link to="/impermeabilizzazione-poliurea" className="hover:text-background transition-colors">Impermeabilizzazione Poliurea</Link></li>
              <li><Link to="/coibentazione-poliuretano" className="hover:text-background transition-colors">Coibentazione Poliuretano</Link></li>
              <li><Link to="/efficientamento-energetico" className="hover:text-background transition-colors">Efficientamento Energetico</Link></li>
              <li><Link to="/pareti-ventilate" className="hover:text-background transition-colors">Pareti Ventilate</Link></li>
              <li><Link to="/rivestimenti-piscine" className="hover:text-background transition-colors">Rivestimenti Piscine</Link></li>
              <li><Link to="/rivestimenti-protettivi" className="hover:text-background transition-colors">Rivestimenti Protettivi Anticorrosivi</Link></li>
              <li><Link to="/restauro-conservativo" className="hover:text-background transition-colors">Restauro Conservativo</Link></li>
              <li><Link to="/finiture-e-rivestimenti" className="hover:text-background transition-colors">Finiture e Rivestimenti</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-5">Contatti</p>
            <ul className="space-y-3 text-background/70 text-sm">
              <li>
                <a href="https://www.google.com/maps/search/?api=1&query=Via+Nino+Bixio+182+Monopoli+BA" target="_blank" rel="noopener noreferrer" data-testid="footer-maps" className="flex items-center gap-2 hover:text-background transition-colors whitespace-nowrap">
                  <MapPin className="w-4 h-4 shrink-0" /> Via Nino Bixio, 182 — Monopoli (BA)
                </a>
              </li>
              <li>
                <div className="flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-1">
                  <a href="tel:+393519261622" data-testid="footer-cellulare" className="flex items-center gap-2 hover:text-background transition-colors">
                    <Phone className="w-4 h-4 shrink-0" /> +39 351 926 1622
                  </a>
                  <a href="tel:+390805211927" data-testid="footer-telefono-ufficio" className="flex items-center gap-2 hover:text-background transition-colors">
                    <Phone className="w-4 h-4 shrink-0" /> 080 521 1927
                  </a>
                </div>
              </li>
              <li>
                <button type="button" onClick={() => openEmail("commerciale@greenhousesolutionsspa.com")} data-testid="footer-email-commerciale" className={mailBtnCls}>
                  <Mail className="w-4 h-4 mt-0.5 shrink-0" /> commerciale@greenhousesolutionsspa.com
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openEmail("amministrazione@greenhousesolutionsspa.com")} data-testid="footer-email-amministrazione" className={mailBtnCls}>
                  <Mail className="w-4 h-4 mt-0.5 shrink-0" /> amministrazione@greenhousesolutionsspa.com
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openEmail("segreteria@greenhousesolutionsspa.com")} data-testid="footer-email-segreteria" className={mailBtnCls}>
                  <Mail className="w-4 h-4 mt-0.5 shrink-0" /> segreteria@greenhousesolutionsspa.com
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={copyPec}
                  data-testid="footer-email-pec"
                  aria-label="Copia PEC negli appunti"
                  className="flex items-start gap-2 hover:text-background transition-colors break-all text-left w-full cursor-pointer bg-transparent p-0 border-0"
                >
                  <Mail className="w-4 h-4 mt-0.5 shrink-0" /> greenhousesolutionsspa@pec.it (PEC)
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-background/20">
          <a
            href="https://pacard.it/vetrina/16026-greenhouse-solutions-spa-imprese-edili-monopoli-ba"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-pacard"
            aria-label="Vetrina PA card: convenzioni e sconti per dipendenti Pubblica Amministrazione"
            className="group flex flex-col items-start sm:flex-row sm:items-center gap-3 sm:gap-6 pb-6 sm:pb-8 mb-6 sm:mb-8 border-b border-background/10 hover:opacity-90 transition-opacity"
          >
            <img
              src="/images/pacard-logo.webp"
              alt="PA card by Convenzioni Dipendenti PA"
              width="640"
              height="176"
              className="w-auto h-auto max-h-8 sm:max-h-10 md:max-h-12 max-w-[160px] sm:max-w-none shrink-0 object-contain"
              loading="lazy"
            />
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">Partnership</p>
              <p className="text-background/70 text-xs sm:text-sm leading-relaxed">
                Convenzione riservata ai dipendenti della Pubblica Amministrazione — vantaggi e sconti dedicati.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-background/60 group-hover:text-primary transition-colors shrink-0">
              Vai alla vetrina <ArrowUpRight className="w-4 h-4" />
            </span>
          </a>
          <div
            data-testid="footer-inprimepay"
            className="flex flex-col items-start sm:flex-row sm:items-center gap-3 sm:gap-6 pb-6 sm:pb-8 mb-6 sm:mb-8 border-b border-background/10"
          >
            <img
              src="/images/inprimepay-partnership.png"
              alt="inprimePay — Paga a rate con Klarna e Alma"
              width="1200"
              height="896"
              className="w-auto h-auto max-h-14 sm:max-h-16 md:max-h-20 max-w-[220px] sm:max-w-none shrink-0 object-contain"
              loading="lazy"
            />
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">Partnership</p>
              <p className="text-background text-sm sm:text-base font-head font-bold leading-tight">
                Realizza il tuo progetto oggi, rateizza l'investimento nel tempo.
              </p>
              <p className="text-background/60 text-xs sm:text-sm leading-relaxed mt-1">
                Finanziamento a rate con Klarna e Alma tramite inprimePay — transazioni verificate e sicure.
              </p>
            </div>
          </div>
          <div className="font-mono text-xs text-background/50 uppercase tracking-widest">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <span>© {new Date().getFullYear()} Greenhouse Solutions SPA</span>
            <div className="flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.testid}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={s.testid}
                  className="w-9 h-9 grid place-items-center border border-background/20 text-background/70 hover:bg-primary hover:border-primary hover:text-primary-foreground transition-colors"
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
          <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div data-testid="footer-legal" className="flex flex-col md:flex-row md:flex-wrap gap-x-6 gap-y-2 text-background/40">
              <span data-testid="footer-piva">P. IVA 08671110727</span>
              <span className="hidden md:inline text-background/20">·</span>
              <span data-testid="footer-rea">REA BA-642120</span>
              <span className="hidden md:inline text-background/20">·</span>
              <span data-testid="footer-capitale">Capitale sociale € 50.000,00</span>
            </div>
            <div className="flex items-center gap-5 shrink-0">
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-primary transition-colors">Area Riservata</Link>
            </div>
          </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
